import {
  buildValueDef,
  buildSelectFieldOption,
  buildUserSelectFieldOption,
  buildTableColFields,
  schemaValueDefFromField,
  contractSplitTableColDefs,
  zzlPreselectValueDef,
  buildMultiFormFieldOption,
  enrichMultiFormItemFields,
} from '../value-def-helpers';

/**
 * 表单控件值结构描述：在 methods 中通过 getValueDef() 动态读取 config / fields
 */
export default {
  methods: {
    _refOf(key) {
      if (!key) {
        return null;
      }
      let r = this.$refs[key];
      return Array.isArray(r) ? r[0] : r;
    },
    /** 从子字段配置 + 已挂载子组件递归收集值定义（容器用） */
    collectFieldsValueDef(fields) {
      const containers = this.$store.getters['forms/containers'];
      const acl = this.acl || {};
      const result = [];
      (fields || []).forEach((f) => {
        if (!f || (f.fullkey && acl[f.fullkey] === 2)) {
          return;
        }
        const ref = this._refOf(f.key);
        if (ref && typeof ref.getValueDef === 'function') {
          const d = ref.getValueDef();
          if (d == null) {
            return;
          }
          if (Array.isArray(d)) {
            result.push(...d);
          } else {
            result.push(d);
          }
          return;
        }
        if (containers.includes(f.control)) {
          result.push(...this.collectFieldsValueDef(f.fields));
          return;
        }
        const schemaDef = schemaValueDefFromField(f, (childFields) =>
          this.collectFieldsValueDef(childFields),
        );
        if (schemaDef) {
          if (Array.isArray(schemaDef)) {
            result.push(...schemaDef);
          } else {
            result.push(schemaDef);
          }
        }
      });
      return result;
    },
    _fieldKey() {
      return this.fkey || (this.$attrs && this.$attrs.fkey);
    },
    _fieldName() {
      return this.label;
    },
    _fieldConfig() {
      return this.config || {};
    },
    valueDefString() {
      return buildValueDef({
        type: 'string',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {...this._fieldConfig()},
      });
    },
    valueDefNumber() {
      return buildValueDef({
        type: 'number',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {...this._fieldConfig()},
      });
    },
    _selectControlName() {
      const o = this.$options || {};
      return o.valueDefControl || o.name || 'UgoSelect';
    },
    valueDefSelect() {
      const cfg = this._fieldConfig();
      const multiple = !!cfg.multiple;
      return buildValueDef({
        type: multiple ? 'array' : 'string',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: buildSelectFieldOption(cfg, this._selectControlName()),
      });
    },
    valueDefCheckArray() {
      const cfg = this._fieldConfig();
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: buildSelectFieldOption(cfg, this._selectControlName()),
      });
    },
    valueDefFile() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          description: '文件列表',
          ...this._fieldConfig(),
        },
      });
    },
    valueDefDatetimeRange() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          length: 2,
          itemType: 'string',
          description: '[开始时间, 结束时间]',
          ...this._fieldConfig(),
        },
      });
    },
    valueDefUserSelect() {
      const cfg = this._fieldConfig();
      const multiple = !!cfg.multiple;
      const users = this.$store && this.$store.getters ?
        this.$store.getters['session/users'] :
        [];
      return buildValueDef({
        type: multiple ? 'array' : 'number',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: buildUserSelectFieldOption(
          {...cfg, control: this._selectControlName()},
          users,
        ),
      });
    },
    valueDefTable() {
      const cols = this._fieldConfig().cols || [];
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {itemType: 'object', ...this._fieldConfig()},
        fields: buildTableColFields(cols),
      });
    },
    valueDefContractSplitTable() {
      return buildValueDef({
        type: 'array',
        key: this._fieldKey(),
        name: this._fieldName(),
        option: {
          itemType: 'object',
          amountKey: this._fieldConfig().amountKey,
        },
        fields: buildTableColFields(contractSplitTableColDefs()),
      });
    },
    valueDefZzlPreselect() {
      return zzlPreselectValueDef({
        key: this._fieldKey(),
        name: this._fieldName(),
        config: this._fieldConfig(),
      });
    },
    valueDefNestedForm() {
      const fieldKey = this._fieldKey();
      const nested = this.$refs.form;
      const formDefFields = (this.formDef && this.formDef.fields) || this.fields || [];
      let childFields = [];
      if (nested && typeof nested.getEditableFieldsDef === 'function') {
        childFields = nested.getEditableFieldsDef();
      }
      if (!childFields.length && formDefFields.length) {
        childFields = this.collectFieldsValueDef(formDefFields);
      }
      return buildValueDef({
        type: 'object',
        key: fieldKey,
        name: this._fieldName(),
        option: {
          model: this._fieldConfig().model,
          description: fieldKey ?
            `内嵌表单，JSON 中键名必须为 "${fieldKey}"（不要用中文名称作键）` :
            '内嵌表单对象',
        },
        fields: childFields,
      });
    },
    _resolveMultiFormItemFields() {
      const forms = this.$refs.form;
      const templateForm = Array.isArray(forms) ?
        forms.find((f) => f && typeof f.getEditableFieldsDef === 'function') :
        forms;
      if (templateForm) {
        return templateForm.getEditableFieldsDef();
      }
      const defFields = this.formDef && this.formDef.fields;
      if (Array.isArray(defFields) && defFields.length) {
        return this.collectFieldsValueDef(defFields);
      }
      return this.collectFieldsValueDef(this.fields || []);
    },
    valueDefMultiForm() {
      const cfg = this._fieldConfig();
      const fieldKey = this._fieldKey();
      const itemFields = enrichMultiFormItemFields(this._resolveMultiFormItemFields(), cfg);
      return buildValueDef({
        type: 'array',
        key: fieldKey,
        name: this._fieldName(),
        option: buildMultiFormFieldOption(cfg, {fieldKey, itemFields}),
        fields: itemFields,
      });
    },
  },
};
